import React from 'react';
import { checkSelectionCount, countSelections, getEffectiveMinSelections } from './minSelections';

/**
 * Cell types of the free-form TABLE attribute.
 *
 * A type lives at two levels: on the column (`tableHead[i].cellType` +
 * `tableHead[i].options`) as the default for every row, and per cell
 * (`table.cellTypes[`${row}_${colId}`]` + `table.cellOptions[...]`) as an
 * override. Every renderer — builder, previews, members portal, PDF converters —
 * resolves `cellTypes[key] || col.cellType || 'text'`, so the column default
 * needs no support anywhere but the builder.
 */
export const CELL_TYPES = {
    TEXT: 'text',
    RADIO: 'radio',
    CHECKBOX: 'checkbox',
    CHECKBOX_GROUP: 'checkboxGroup',
    SELECT: 'select',
    MULTI_SELECT: 'multiSelect',
};

export const cellTypeList = [
    { id: CELL_TYPES.TEXT, name: 'Text' },
    { id: CELL_TYPES.RADIO, name: 'Radio' },
    { id: CELL_TYPES.CHECKBOX, name: 'Checkbox (Single)' },
    { id: CELL_TYPES.CHECKBOX_GROUP, name: 'Checkbox Group' },
    { id: CELL_TYPES.SELECT, name: 'Select' },
    { id: CELL_TYPES.MULTI_SELECT, name: 'Multi Select' },
];

const OPTION_CELL_TYPES = [CELL_TYPES.RADIO, CELL_TYPES.CHECKBOX_GROUP, CELL_TYPES.SELECT, CELL_TYPES.MULTI_SELECT];

/** Types whose cells pick from a list of options. */
export const isOptionCellType = (type) => OPTION_CELL_TYPES.includes(type);

/** The blank value a cell of `type` starts with. */
export const getEmptyCellValue = (type) => {
    if (type === CELL_TYPES.CHECKBOX) return false;
    if (type === CELL_TYPES.CHECKBOX_GROUP || type === CELL_TYPES.MULTI_SELECT) return [];
    if (type === CELL_TYPES.RADIO || type === CELL_TYPES.SELECT) return null;
    return '';
};

/**
 * Whether a body cell is Read Only: its template value (e.g. a label next to the
 * cells staff fill) is shown and cannot be changed while filling. Set per cell only
 * (`table.cellReadOnly[`${row}_${colId}`] = true`); the whole element has its own
 * `properties.readOnly`. The author still edits the cell's value in the builder.
 */
export const isCellReadOnly = (table, col, rowIndex) => !!table?.cellReadOnly?.[`${rowIndex}_${col.id}`];

/**
 * Whether a body cell must be filled. Like the type, `required` lives on the
 * column (`tableHead[i].required`) as the default for every row, and per cell
 * (`table.cellRequired[`${row}_${colId}`]`, true or false) as an override.
 * A Read Only cell is never required: staff cannot fill it.
 */
export const isCellRequired = (table, col, rowIndex) => {
    if (isCellReadOnly(table, col, rowIndex)) return false;
    const override = table?.cellRequired?.[`${rowIndex}_${col.id}`];
    return override !== undefined ? !!override : !!col?.required;
};

const isValueEmpty = (value, type) => {
    if (type === CELL_TYPES.CHECKBOX) return !value;
    if (type === CELL_TYPES.CHECKBOX_GROUP || type === CELL_TYPES.MULTI_SELECT) return !Array.isArray(value) || !value.length;
    if (type === CELL_TYPES.RADIO || type === CELL_TYPES.SELECT) return !value;
    if (typeof value !== 'string') return true;
    // Text cells may hold HTML (mentions, pasted rich text).
    return !value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
};

const isHiddenByMerge = (mergedCells, rowIndex, colIndex) =>
    Object.values(mergedCells || {}).some(
        (m) =>
            rowIndex >= m.startRow &&
            rowIndex < m.startRow + m.rowSpan &&
            colIndex >= m.startCol &&
            colIndex < m.startCol + m.colSpan &&
            !(rowIndex === m.startRow && colIndex === m.startCol)
    );

const isMultiChoiceCellType = (type) => type === CELL_TYPES.CHECKBOX_GROUP || type === CELL_TYPES.MULTI_SELECT;

/**
 * Minimum selections of a body cell: applies to Checkbox group / Multi select
 * cells, Required or not (see `minSelections.js` for the rule). Like Required,
 * it lives on the column (`tableHead[i].minSelections`) as the default for
 * every row, and per cell (`table.cellMinSelections[`${row}_${colId}`]`, a
 * whole number — 0 clears the column's minimum) as an override.
 */
export const getCellMinSelections = (table, col, rowIndex) => {
    const override = table?.cellMinSelections?.[`${rowIndex}_${col.id}`];
    return override !== undefined && override !== null ? override : col?.minSelections;
};

/** Whether one cell (or one half of a split cell) fails validation. */
const isCellInvalid = (value, type, required, rawMin, optionCount) => {
    if (isMultiChoiceCellType(type)) {
        const min = getEffectiveMinSelections(rawMin, optionCount);
        return !!checkSelectionCount(countSelections(value), { required, min });
    }
    return required && isValueEmpty(value, type);
};

/**
 * Keys (`${row}_${colId}`) of the cells in `rows` that block saving: required
 * cells that are still empty, and multi-choice cells with fewer selections
 * than their minimum (an optional one may stay empty). A split cell is
 * checked half by half; a cell covered by a merge is skipped.
 */
export const getMissingRequiredCells = (table = {}, rows = []) => {
    const missing = new Set();
    const tableHead = table.tableHead || [];
    const cellTypes = table.cellTypes || {};
    const cellOptions = table.cellOptions || {};
    const splitCells = table.splitCells || {};

    rows.forEach((row, rowIndex) => {
        tableHead.forEach((col, colIndex) => {
            // Read Only: staff cannot change it, so neither Required nor a minimum applies.
            if (isCellReadOnly(table, col, rowIndex)) return;
            const required = isCellRequired(table, col, rowIndex);
            const rawMin = getCellMinSelections(table, col, rowIndex);
            if ((!required && !rawMin) || isHiddenByMerge(table.mergedCells, rowIndex, colIndex)) return;
            const key = `${rowIndex}_${col.id}`;
            const type = cellTypes[key] || col.cellType || CELL_TYPES.TEXT;
            const value = row?.[col.id];
            const optionCountOf = (k) => (cellOptions[k] || cellOptions[key] || col.options || []).length;
            const invalid = splitCells[key]
                ? ['left', 'right'].some((side) =>
                      isCellInvalid(value?.[side], cellTypes[`${key}_${side}`] || type, required, rawMin, optionCountOf(`${key}_${side}`))
                  )
                : isCellInvalid(value, type, required, rawMin, optionCountOf(key));
            if (invalid) missing.add(key);
        });
    });
    return missing;
};

export const CellTypeIcon = ({ type, color = '#344054', size = 14 }) => {
    const common = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: color,
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        style: { flexShrink: 0 },
    };

    switch (type) {
        case CELL_TYPES.RADIO:
            return (
                <svg {...common}>
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4" fill={color} />
                </svg>
            );
        case CELL_TYPES.CHECKBOX:
            return (
                <svg {...common}>
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <path d="M9 12l2 2 4-4" />
                </svg>
            );
        case CELL_TYPES.CHECKBOX_GROUP:
            return (
                <svg {...common}>
                    <rect x="3" y="3" width="8" height="8" rx="2" />
                    <path d="M5 7l1.5 1.5L9 5.5" />
                    <rect x="13" y="3" width="8" height="8" rx="2" />
                    <rect x="3" y="13" width="8" height="8" rx="2" />
                </svg>
            );
        case CELL_TYPES.SELECT:
            return (
                <svg {...common}>
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <polyline points="8 10 12 14 16 10" />
                </svg>
            );
        case CELL_TYPES.MULTI_SELECT:
            return (
                <svg {...common}>
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <polyline points="8 10 12 14 16 10" />
                    <line x1="8" y1="17" x2="16" y2="17" />
                </svg>
            );
        default:
            return (
                <svg {...common}>
                    <path d="M4 7V4h16v3" />
                    <path d="M12 4v16" />
                    <path d="M8 20h8" />
                </svg>
            );
    }
};
